import * as React from 'react';
import styled from '@emotion/styled';
import { tokens, IconButton, Tooltip, Stack } from '@hotjar/hotsauce';
import { limitLength } from '@hotjar/insights-common/lib/string';
import { timeAgo } from '@hotjar/webapp/lib/time';
import { ResponseCardAddComment } from '@hotjar/feedback';
import { RecordingLink } from '@hotjar/feedback/components/common/RecordingLink';
import type { VotingAction } from '@hotjar/feedback/domain/common/Responses/VotingAction';
import type { FeedbackResponseDto } from '@hotjar/feedback/domain/IncomingFeedback/FeedbackResponseDto';
import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import { EmotionIcon } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import { ResponseFeedbackTooltip } from '@hotjar/webapp/features/Feedback/Responses/ResponseFeedbackTooltip';
import { stripProtocol } from '@hotjar/webapp/utils/urls';

import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { useTranslation } from '@hotjar/translations';
import { BrowserIcon } from './icons/BrowserIcon';
import { DeviceIcon } from './icons/DeviceIcon';
import { SystemIcon } from './icons/SystemIcon';

const ScreenshotContainer = styled.div`
  padding: 0;
  background: ${tokens.color.neutral_400};
  position: relative;

  img {
    min-width: 100%;
    max-width: 100%;
    height: 180px;
    min-height: 180px;
    max-height: 180px;
    visibility: hidden;
    border-radius: ${tokens.radius.m} ${tokens.radius.m} 0 0;
  }
`;
const ScreenshotCover = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-size: cover;
  background-position: left center;
  opacity: 0.92;
`;
const StyledStack = styled(Stack)`
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
`;
const Details = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;
const CreationTime = styled.span`
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};
  margin-top: ${tokens.spacer.x1};
`;
const TechDetails = styled.div`
  display: flex;
  align-items: center;
`;
const Comment = styled.div`
  ${tokens.text('body2')};
  word-break: break-word;

  &::before {
    content: '\\201c';
    margin-right: 0.3em;
  }

  &::after {
    content: '\\201d';
    margin-left: 0.3em;
  }

  &::before,
  &::after {
    display: inline;
    ${tokens.text('body2')};
    vertical-align: middle;
  }
`;
const ResponseUrl = styled.div`
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Actions = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
const FeedbackTooltipWrapper = styled.div`
  display: inline-flex;
  vertical-align: middle;
  margin-right: ${tokens.spacer.x0};
  margin-left: ${tokens.spacer.x0};
  cursor: default;
`;
const FeedbackButton = styled.div`
  display: inline-flex;
  width: ${tokens.spacer.x3};
  height: ${tokens.spacer.x3};
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const CommentsCount = styled.span`
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};
  cursor: pointer;
`;
const Card = styled.div`
  background: ${tokens.color.neutral_100};
  position: relative;
  border-radius: ${tokens.radius.m};
  width: 260px;
  margin-bottom: ${tokens.spacer.x3};
  box-shadow: 0 0 0 0 ${tokens.color.neutral_100};
  transition: all 0.12s linear;
  cursor: pointer;

  ${TechDetails} {
    display: none;
  }

  &:hover {
    ${tokens.elevation(2)};
    ${TechDetails} {
      display: inline-block;
    }
  }
`;

interface ResponseCardProps {
  emotionStyle: EmotionStyle;
  feedbackWidgetId: number;
  onAddComment: (response: FeedbackResponseDto) => void;
  onClick: (response: FeedbackResponseDto) => void;
  onDelete: (response: FeedbackResponseDto) => void;
  onFeedbackVote: (votingAction: VotingAction, responseId: number) => void;
  onShowComments: (response: FeedbackResponseDto) => void;
  response: FeedbackResponseDto;
  siteId: number;
  coveragePercentage: number;
}

export function ResponseCard({
  emotionStyle,
  feedbackWidgetId,
  onAddComment,
  onClick,
  onDelete,
  onShowComments,
  onFeedbackVote,
  response,
  siteId,
  coveragePercentage,
}: ResponseCardProps): JSX.Element {
  const {
    browser,
    comment_count: commentCount,
    content: { emotion: score, message },
    created_epoch_time: createdTime,
    device,
    id: responseId,
    os,
    response_url: responseUrl,
    thumbnail_url: thumbnailUrl,
    voting_action: feedbackVotingAction,
    window_size: windowSize,
    recording_url: recordingUrl,
  } = response;
  const { hasPermission } = useAuthorization();
  const hasResponseDeletePermission = hasPermission(
    'site',
    `${siteId}`,
    'feedbacks-responses-delete',
  );
  const { t } = useTranslation('feedback', { keyPrefix: 'feedback-card' });

  function handleAddComment(e: { stopPropagation: () => void }) {
    e.stopPropagation();
    onAddComment(response);
  }

  function showComments(e: { stopPropagation: () => void }) {
    e.stopPropagation();
    onShowComments(response);
  }

  function handleDelete(e: React.SyntheticEvent<HTMLButtonElement>) {
    e.stopPropagation();
    onDelete(response);
  }

  return (
    <Card onClick={() => onClick(response)}>
      {thumbnailUrl && (
        <ScreenshotContainer>
          <img src={thumbnailUrl} alt="" />
          <ScreenshotCover style={{ backgroundImage: `url('${thumbnailUrl}')` }} />
        </ScreenshotContainer>
      )}
      <StyledStack spacing="x1" direction="column">
        <Details>
          <CreationTime>{timeAgo(createdTime)}</CreationTime>
          <TechDetails>
            <DeviceIcon
              device={device}
              tooltip={t('device.device-details', { x: windowSize[0], y: windowSize[1], device })}
            />
            <BrowserIcon browser={browser} />
            <SystemIcon system={os} />
          </TechDetails>
        </Details>
        <EmotionIcon
          compactStars
          emotionStyle={emotionStyle}
          score={score}
          size={emotionStyle === 'star' ? 's' : 'm'}
        />
        {message && message.length > 0 && <Comment>{limitLength(message, 80)}</Comment>}
        <ResponseUrl>{stripProtocol(responseUrl)}</ResponseUrl>
        <Actions>
          <RecordingLink
            url={recordingUrl}
            opener="incoming-feedback-response"
            type="icon"
            trackTriggerView="feedback.responses"
            trackUiPosition="incoming_feedback_response_card_view_play_icon"
            coveragePercentage={coveragePercentage}
          />
          <ResponseCardAddComment onClick={handleAddComment} />
          {commentCount > 0 && (
            <Tooltip text={t('comments.see-comments')}>
              <CommentsCount onClick={showComments}>{commentCount}</CommentsCount>
            </Tooltip>
          )}
          <FeedbackTooltipWrapper onClick={(e) => e.stopPropagation()}>
            <ResponseFeedbackTooltip
              votingAction={feedbackVotingAction}
              siteId={siteId}
              feedbackId={feedbackWidgetId}
              responseId={responseId}
              onChange={onFeedbackVote}
              distance={6}
              side="right"
              renderReference={({ renderDefaultReference }) => (
                <FeedbackButton>{renderDefaultReference()}</FeedbackButton>
              )}
            />
          </FeedbackTooltipWrapper>
          {hasResponseDeletePermission && (
            <IconButton
              description={t('delete.delete-description')}
              icon="trash"
              tooltipText={t('delete.delete-tooltip')}
              required-access="write"
              kind="ghost"
              size="s"
              onClick={handleDelete}
            />
          )}
        </Actions>
      </StyledStack>
    </Card>
  );
}
